import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { podcastApi } from '../services/api';
import { Share2, Calendar, Clock, Search, Mic, Radio, Zap, Play, Globe, Trash2, Plus, Lock } from 'lucide-react';
import SocialShareDialog from '../components/SocialShareDialog';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

const MyPodcastsPage = () => {
  const [podcasts, setPodcasts] = useState([]);
  const [filteredPodcasts, setFilteredPodcasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareTarget, setShareTarget] = useState(null);
  const [generatingProgress, setGeneratingProgress] = useState({});
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  
  console.log('MyPodcastsPage render - user:', user, 'loading:', loading);

  // Refresh user/credits on mount
  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  // Fetch podcasts whenever user becomes available or changes
  useEffect(() => {
    const fetchPodcasts = async () => {
      console.log('MyPodcasts fetch effect running, user:', user);
      if (!user) {
        console.log('No user found, skipping fetch');
        return;
      }
      try {
        console.log('Fetching podcasts...');
        setLoading(true);
        const res = await podcastApi.getMyPodcasts();
        console.log('Podcasts fetched:', res.data);
        setPodcasts(res.data);
        setFilteredPodcasts(res.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching podcasts:', err);
        if (err.response?.status === 401) {
          setError('Unauthorized: Please log in to view your podcasts.');
        } else if (err.response?.status === 403) {
          setError('Access denied. Please log in with a valid account.');
        } else {
          setError('Failed to fetch podcasts. Please try again later.');
        }
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchPodcasts();
  }, [user]);

  // Poll generating podcasts for progress
  useEffect(() => {
    const generatingPodcasts = podcasts.filter(p => p.status === 'generating');
    if (generatingPodcasts.length === 0) return;

    const statusLabel = (code) => {
      if (code >= 100) return '🎉 Wrapping up the broadcast booth...';
      if (code >= 80) return '🎙️ Recording the final takes...';
      if (code >= 60) return '🔊 Laying down the audio track...';
      if (code >= 30) return '📝 Writing witty commentary...';
      if (code >= 10) return '⚾ Studying the box score like a pro scout...';
      return '🎭 Setting up the broadcast booth...';
    };

    const interval = setInterval(async () => {
      const updates = await Promise.allSettled(
        generatingPodcasts.map(p => podcastApi.checkStatus(p.autocontentRequestId))
      );

      let anyCompleted = false;
      const progressPatch = {};

      updates.forEach((result, i) => {
        if (result.status !== 'fulfilled') return;
        const data = result.value;
        const pod = generatingPodcasts[i];
        const code = data.autocontentStatusCode ?? 0;
        progressPatch[pod._id] = {
          progress: Math.min(code, 99),
          label: statusLabel(code)
        };
        if (data.status === 'completed' || data.status === 'failed') {
          anyCompleted = true;
        }
      });

      setGeneratingProgress(prev => ({ ...prev, ...progressPatch }));

      if (anyCompleted) {
        clearInterval(interval);
        const res = await podcastApi.getMyPodcasts();
        setPodcasts(res.data);
        setFilteredPodcasts(res.data);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [podcasts]);

  // Filter podcasts based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredPodcasts(podcasts);
    } else {
      const filtered = podcasts.filter(podcast => 
        podcast.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        podcast.teamName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        new Date(podcast.createdAt).toLocaleDateString().includes(searchTerm)
      );
      setFilteredPodcasts(filtered);
    }
  }, [searchTerm, podcasts]);

  // Handle delete podcast
  const handleDeletePodcast = async (podcastId) => {
    console.log('Attempting to delete podcast:', podcastId);
    try {
      console.log('Calling deletePodcast API...');
      const response = await podcastApi.deletePodcast(podcastId);
      console.log('Delete response:', response);
      
      // Remove from local state
      const updatedPodcasts = podcasts.filter(p => p._id !== podcastId);
      setPodcasts(updatedPodcasts);
      setFilteredPodcasts(updatedPodcasts.filter(podcast => 
        !searchTerm.trim() || 
        podcast.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        podcast.teamName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        new Date(podcast.createdAt).toLocaleDateString().includes(searchTerm)
      ));
      setDeleteConfirm(null);
      console.log('Podcast deleted successfully from UI');
    } catch (error) {
      console.error('Error deleting podcast:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      alert('Failed to delete podcast. Please try again.');
    }
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Toggle publish/unpublish
  const handleTogglePublish = async (podcast) => {
    try {
      if (podcast.isPublic) {
        // Unpublish
        await podcastApi.unpublishPodcast(podcast._id);
        setPodcasts(prev => prev.map(p => 
          p._id === podcast._id 
            ? { ...p, isPublic: false }
            : p
        ));
        setFilteredPodcasts(prev => prev.map(p => 
          p._id === podcast._id 
            ? { ...p, isPublic: false }
            : p
        ));
      } else {
        // Publish
        const res = await podcastApi.publishPodcast(podcast._id);
        setPodcasts(prev => prev.map(p => 
          p._id === podcast._id 
            ? { ...p, isPublic: true, shareId: res.shareId }
            : p
        ));
        setFilteredPodcasts(prev => prev.map(p => 
          p._id === podcast._id 
            ? { ...p, isPublic: true, shareId: res.shareId }
            : p
        ));
      }
    } catch (error) {
      console.error('Error toggling publish status:', error);
      alert('Failed to update sharing status. Please try again.');
    }
  };

  // Handle sharing (mobile uses Web Share, desktop opens dialog)
  const handleShare = async (podcast) => {
    const publicUrl = `${window.location.origin}/share/${podcast.shareId}`;
    const mobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

    try {
      if (navigator.share && mobile) {
        await navigator.share({
          title: podcast.title,
          url: publicUrl,
        });
      } else {
        setShareTarget(podcast);
        setShareOpen(true);
      }
    } catch (error) {
      console.error('Error sharing:', error);
      setShareTarget(podcast);
      setShareOpen(true);
    }
  };

  // Handle card click - navigate to player page
  const handleCardClick = (podcast) => {
    if (podcast.status === 'completed') {
      if (podcast.isPublic && podcast.shareId) {
        // Use public route for public podcasts
        navigate(`/p/${podcast.shareId}`);
      } else {
        // Use private route for private podcasts
        navigate(`/my-podcasts/${podcast._id}`);
      }
    }
  };

  // Format duration helper
  const formatDuration = (seconds) => {
    if (!seconds) return '';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-blue-950 to-slate-800 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-royal-blue to-blue-400 flex items-center justify-center mx-auto mb-4 shadow-xl">
            <Radio className="h-8 w-8 text-white animate-pulse" />
          </div>
          <p className="text-gray-400 font-medium">Loading your library...</p>
        </div>
      </div>
    );
  }

  if (error) {
    if (error.includes('Unauthorized') || error.includes('401') || error.includes('log in')) {
      return (
        <div className="min-h-screen bg-gradient-to-b from-gray-900 via-blue-950 to-slate-800 flex items-center justify-center px-4">
          <div className="text-center max-w-sm">
            <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-4">
              <Mic className="h-8 w-8 text-gray-300" />
            </div>
            <h2 className="text-2xl font-black text-white mb-2">Sign In Required</h2>
            <p className="text-gray-400 mb-6">Log in to access your podcast library.</p>
            <a
              href={`${API_BASE_URL}/auth/google`}
              className="inline-flex items-center gap-2 bg-royal-blue hover:bg-blue-800 text-white font-bold py-3 px-6 rounded-xl transition-colors shadow-lg"
            >
              Login with Google
            </a>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-blue-950 to-slate-800 flex items-center justify-center px-4">
        <div className="text-center max-w-sm">
          <p className="text-red-400 font-medium">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-blue-950 to-slate-800">
      {/* Dark hero header */}
      <div className="bg-gradient-to-r from-gray-900 via-blue-950 to-gray-900 border-b-4 border-yellow-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <div className="w-10 h-10 rounded-full bg-yellow-400 flex items-center justify-center shadow-lg">
                  <Radio className="h-5 w-5 text-gray-900" />
                </div>
                <span className="text-yellow-400 font-bold text-sm uppercase tracking-widest">My Library</span>
              </div>
              <h1 className="text-3xl sm:text-4xl font-black text-white tracking-tight">My Podcasts</h1>
              <p className="text-blue-300 mt-1 text-sm">{podcasts.length} GameCast{podcasts.length !== 1 ? 's' : ''} in your collection</p>
            </div>
            <Link
              to="/create"
              className="inline-flex items-center gap-2 bg-yellow-400 hover:bg-yellow-300 text-gray-900 font-black py-3 px-6 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105 shrink-0"
            >
              <Plus className="h-5 w-5" />New GameCast
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {podcasts.length === 0 ? (
          /* ── Empty State ── */
          <div className="flex flex-col items-center justify-center py-24">
            <div className="relative mb-6">
              <div className="absolute inset-0 rounded-full bg-yellow-400/20 blur-2xl scale-150" />
              <div className="relative w-20 h-20 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-500 flex items-center justify-center shadow-2xl">
                <Mic className="h-10 w-10 text-gray-900" />
              </div>
            </div>
            <h3 className="text-2xl font-black text-white mb-2">No GameCasts Yet</h3>
            <p className="text-gray-400 text-center max-w-sm mb-8">Upload a box score and create your first AI-powered sports podcast in minutes.</p>
            <Link
              to="/create"
              className="inline-flex items-center gap-2 bg-royal-blue hover:bg-blue-800 text-white font-black py-3 px-8 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl text-base"
            >
              <Zap className="h-5 w-5" />Create Your First GameCast
            </Link>
          </div>
        ) : (
          <>
            {/* Search & stats bar */}
            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between mb-6">
              <div className="relative w-full sm:max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Search your library..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="w-full pl-10 pr-4 py-2.5 bg-white/10 border-2 border-white/20 focus:border-blue-400 rounded-xl text-sm text-white placeholder-gray-400 outline-none transition-colors"
                />
              </div>
              <p className="text-sm text-gray-400 shrink-0">
                {searchTerm
                  ? `${filteredPodcasts.length} of ${podcasts.length} results`
                  : `${podcasts.length} total`}
              </p>
            </div>

            {/* No search results */}
            {filteredPodcasts.length === 0 ? (
              <div className="text-center py-16">
                <Search className="mx-auto h-10 w-10 text-gray-500 mb-3" />
                <h3 className="font-bold text-gray-300 mb-1">No results for "{searchTerm}"</h3>
                <button onClick={() => setSearchTerm('')} className="text-sm text-blue-400 hover:underline font-medium mt-1">Clear search</button>
              </div>
            ) : (
              /* ── Podcast Grid ── */
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredPodcasts.map((podcast) => (
                  <div
                    key={podcast._id}
                    className={`group bg-gradient-to-br from-blue-900/60 to-slate-800/80 backdrop-blur-sm rounded-2xl border border-blue-700/30 shadow-xl overflow-hidden transition-all duration-300 ${
                      podcast.status === 'completed' ? 'cursor-pointer hover:shadow-2xl hover:-translate-y-1 hover:from-blue-800/70 hover:to-slate-700/90 hover:border-blue-500/50' : ''
                    }`}
                    onClick={() => handleCardClick(podcast)}
                  >
                    {/* Card top accent bar */}
                    <div className={`h-1.5 w-full ${
                      podcast.status === 'generating' ? 'bg-gradient-to-r from-yellow-400 to-orange-400 animate-pulse' :
                      podcast.status === 'completed'  ? 'bg-gradient-to-r from-royal-blue to-electric-green' :
                                                        'bg-red-400'
                    }`} />

                    <div className="p-5">
                      {/* Header row */}
                      <div className="flex items-center justify-between mb-3">
                        {/* Status badge */}
                        {podcast.status === 'generating' ? (
                          <span className="inline-flex items-center gap-1.5 text-xs font-bold bg-yellow-400/20 text-yellow-300 border border-yellow-400/30 rounded-full px-2.5 py-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 animate-ping" />Generating...
                          </span>
                        ) : podcast.status === 'completed' ? (
                          podcast.isPaid ? (
                            <span className="inline-flex items-center gap-1.5 text-xs font-bold bg-electric-green/20 text-electric-green border border-electric-green/30 rounded-full px-2.5 py-1">
                              <span className="w-1.5 h-1.5 rounded-full bg-electric-green" />Ready
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 text-xs font-bold bg-yellow-400/20 text-yellow-300 border border-yellow-400/30 rounded-full px-2.5 py-1">
                              <Lock className="h-3 w-3" />Preview
                            </span>
                          )
                        ) : (
                          <span className="inline-flex items-center gap-1.5 text-xs font-bold bg-red-500/20 text-red-400 border border-red-500/30 rounded-full px-2.5 py-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-red-500" />Failed
                          </span>
                        )}

                        <div className="flex items-center gap-2">
                          {podcast.isPublic && (
                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-blue-300 bg-blue-500/20 border border-blue-400/30 rounded-full px-2 py-0.5">
                              <Globe className="h-3 w-3" />Public
                            </span>
                          )}
                          {/* Delete */}
                          <button
                            onClick={(e) => { e.stopPropagation(); setDeleteConfirm(podcast._id); }}
                            className="w-7 h-7 rounded-lg flex items-center justify-center text-gray-500 hover:text-red-400 hover:bg-red-500/20 transition-all duration-200 opacity-0 group-hover:opacity-100"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>

                      {/* Mic icon + title */}
                      <div className="flex items-start gap-3 mb-3">
                        <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center shrink-0 mt-0.5">
                          <Mic className="h-5 w-5 text-blue-300" />
                        </div>
                        <div>
                          <h3 className="font-black text-white text-base leading-snug group-hover:text-yellow-300 transition-colors duration-200">
                            {podcast.opponentName ? `${podcast.teamName} vs. ${podcast.opponentName}` : podcast.title}
                          </h3>
                          {podcast.audioTitle && (
                            <p className="text-xs text-blue-300 mt-0.5 line-clamp-2 italic">
                              {podcast.audioTitle}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Meta */}
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-400 mb-4">
                        <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{new Date(podcast.createdAt).toLocaleDateString()}</span>
                        {podcast.teamName && <span className="flex items-center gap-1">🏆 {podcast.teamName}</span>}
                        {podcast.duration && <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{formatDuration(podcast.duration)}</span>}
                      </div>

                      {/* Progress bar – generating only */}
                      {podcast.status === 'generating' && (() => {
                        const prog = generatingProgress[podcast._id];
                        const pct = prog ? prog.progress : 0;
                        const label = prog ? prog.label : '🎭 Setting up the broadcast booth...';
                        return (
                          <div className="mt-1 mb-1">
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-xs text-yellow-300 font-medium truncate pr-2">{label}</span>
                              <span className="text-xs font-bold text-yellow-300 shrink-0">{pct}%</span>
                            </div>
                            <div className="w-full bg-yellow-400/20 rounded-full h-2 overflow-hidden">
                              <div
                                className="h-2 rounded-full bg-gradient-to-r from-yellow-400 to-orange-400 transition-all duration-700"
                                style={{ width: `${Math.max(pct, 3)}%` }}
                              />
                            </div>
                          </div>
                        );
                      })()}

                      {/* Footer controls – completed only */}
                      {podcast.status === 'completed' && (
                        <div className="flex items-center justify-between pt-3 border-t border-white/10">
                          {/* Play hint */}
                          <span className="inline-flex items-center gap-1 text-xs text-gray-500 group-hover:text-blue-300 transition-colors">
                            <Play className="h-3.5 w-3.5" />{podcast.isPaid ? 'Click to listen' : 'Preview • click to unlock'}
                          </span>

                          {podcast.isPaid ? (
                            <div className="flex items-center gap-2">
                              {/* Public toggle */}
                              <button
                                onClick={(e) => { e.stopPropagation(); handleTogglePublish(podcast); }}
                                title={podcast.isPublic ? 'Make private' : 'Make public'}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-300 focus:outline-none ${
                                  podcast.isPublic ? 'bg-electric-green' : 'bg-gray-300 hover:bg-gray-400'
                                }`}
                              >
                                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-300 ${podcast.isPublic ? 'translate-x-6' : 'translate-x-1'}`} />
                              </button>

                              {/* Share button */}
                              {podcast.isPublic && podcast.shareId && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleShare(podcast); }}
                                  className="inline-flex items-center gap-1.5 bg-royal-blue hover:bg-blue-800 text-white text-xs font-bold px-3 py-1.5 rounded-lg transition-colors shadow-sm"
                                >
                                  <Share2 className="h-3.5 w-3.5" />Share
                                </button>
                              )}
                            </div>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 text-xs font-bold text-yellow-300 bg-yellow-400/15 border border-yellow-400/30 rounded-lg px-2.5 py-1">
                              <Lock className="h-3.5 w-3.5" />Unlock to share
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 px-4">
          <div className="bg-gray-900 rounded-2xl shadow-2xl max-w-sm w-full p-6 border border-white/10">
            <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
              <Trash2 className="h-6 w-6 text-red-400" />
            </div>
            <h3 className="text-lg font-black text-white text-center mb-2">Delete Podcast?</h3>
            <p className="text-sm text-gray-400 text-center mb-6">This action is permanent and cannot be undone.</p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 py-2.5 text-sm font-bold text-gray-300 bg-white/10 hover:bg-white/20 rounded-xl transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeletePodcast(deleteConfirm)}
                className="flex-1 py-2.5 text-sm font-bold text-white bg-red-600 hover:bg-red-500 rounded-xl transition-colors shadow-md"
              >
                Delete Forever
              </button>
            </div>
          </div>
        </div>
      )}

      <SocialShareDialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        shareUrl={shareTarget ? `${window.location.origin}/share/${shareTarget.shareId}` : ''}
        title={shareTarget ? shareTarget.title : ''}
        text=''
      />
    </div>
  );
};

export default MyPodcastsPage;
